import { Box, Stack, Typography, Avatar, Chip, Divider, Skeleton } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

const primaryBlueDark = "#185FA5";
const primaryBlueLight = "#E6F1FB";

const FieldBlock = ({ label, value, isLink = false }) => (
	<Box mb={1.75}>
		<Typography
			sx={{
				fontSize: { xs: 11, sm: 13 },
				color: "text.secondary",
				lineHeight: 1.4,
				mb: 0.25,
				whiteSpace: "nowrap",
				overflow: "hidden",
				textOverflow: "ellipsis",
			}}
		>
			{label}
		</Typography>
		<Typography
			variant="body2"
			sx={{
				fontWeight: 500,
				color: isLink ? primaryBlueDark : "text.primary",
				fontSize: "0.875rem",
			}}
		>
			{value || "—"}
		</Typography>
	</Box>
);

const FieldBlockSkeleton = () => (
	<Box mb={1.75}>
		<Skeleton variant="text" width="50%" height={18} sx={{ mb: 0.25 }} />
		<Skeleton variant="text" width="80%" height={20} />
	</Box>
);

const AgentOverview = ({ agent, loading }) => {
	if (loading || !agent) {
		return (
			<Box
				sx={{
					p: 3,
					backgroundColor: "background.paper",
					border: "1px solid rgba(29,84,162,0.1)",
					borderRadius: "12px",
					boxShadow: "0 1px 8px rgba(29,84,162,0.06)",
				}}
			>
				<Stack direction={{ xs: "column", sm: "row" }} gap={3} alignItems="flex-start">
					<Skeleton variant="circular" width={120} height={120} sx={{ flexShrink: 0 }} />
					<Box
						sx={{
							flex: 1,
							display: "grid",
							gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
							gap: { xs: 1, sm: 0 },
							width: "100%",
						}}
					>
						<Box>
							<FieldBlockSkeleton />
							<FieldBlockSkeleton />
							<FieldBlockSkeleton />
						</Box>
						<Box>
							<FieldBlockSkeleton />
							<FieldBlockSkeleton />
							<FieldBlockSkeleton />
						</Box>
						<Box>
							<FieldBlockSkeleton />
							<FieldBlockSkeleton />
						</Box>
						<Box ml={{ xs: 0, sm: 7 }}>
							<FieldBlockSkeleton />
							<Skeleton variant="text" width="60%" height={16} sx={{ mb: 0.5 }} />
							<Skeleton variant="rounded" width={90} height={24} />
						</Box>
					</Box>
				</Stack>
			</Box>
		);
	}

	const initials = agent?.fullName
		? agent?.fullName
				.split(" ")
				.map((n) => n[0])
				.slice(0, 2)
				.join("")
		: "?";

	return (
		<Box
			sx={{
				p: 3,
				backgroundColor: "background.paper",
				border: "1px solid rgba(29,84,162,0.1)",
				borderRadius: "12px",
				boxShadow: "0 1px 8px rgba(29,84,162,0.06)",
			}}
		>
			<Stack direction={{ xs: "column", sm: "row" }} gap={3} alignItems="flex-start">
				{/* Avatar */}
				<Avatar
					src={agent?.avatarUrl || undefined}
					sx={{
						width: 120,
						height: 120,
						bgcolor: primaryBlueLight,
						border: `1.5px solid ${primaryBlueDark}`,
						fontSize: "1.75rem",
						color: primaryBlueDark,
						flexShrink: 0,
					}}
				>
					{!agent?.avatarUrl && initials}
				</Avatar>

				{/* All fields in 4 equal columns */}
				<Box
					sx={{
						flex: 1,
						display: "grid",
						gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
						gap: { xs: 1, sm: 0 },
					}}
				>
					<Box>
						<FieldBlock label="Full Name" value={agent?.fullName} />
						<FieldBlock label="Date of Birth" value={agent?.dob} />
						<FieldBlock label="Phone" value={agent?.phone} />
					</Box>
					<Box>
						<FieldBlock label="NPN" value={agent?.npn} />
						<FieldBlock label="License Number" value={agent?.licenseNumber} />
						<FieldBlock label="Agency Name" value={agent?.agencyName} />
					</Box>
					<Box>
						<FieldBlock label="Address" value={agent?.address} />
						<FieldBlock label="Email" value={agent?.email} isLink />
					</Box>
					<Box ml={7}>
						<FieldBlock label="Upline Agent" value={agent?.upline} />
						<Box mb={1.75}>
							<Typography
								variant="caption"
								sx={{
									display: "block",
									textTransform: "uppercase",
									letterSpacing: "0.05em",
									color: "text.secondary",
									mb: 0.5,
									fontSize: "0.7rem",
								}}
							>
								License Status
							</Typography>
							<Chip
								icon={<CheckCircleOutlineIcon sx={{ fontSize: "0.9rem !important" }} />}
								label={agent?.licenseStatus || "Unknown"}
								size="small"
								sx={{
									backgroundColor: "#EAF3DE",
									color: "#3B6D11",
									fontWeight: 500,
									fontSize: "0.75rem",
									height: 24,
									"& .MuiChip-icon": { color: "#3B6D11" },
								}}
							/>
						</Box>
					</Box>
				</Box>
			</Stack>
		</Box>
	);
};
export default AgentOverview;
